import { Modal, ModalBody, ModalFooter, ModalHeader } from "shared/components/modal";
import { useContactContextService } from "./service";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import FormContainer from "shared/form";
import FormTextField from "shared/form/fields/textField";
import { leadCaptureFormFields } from "./utils";
import FormSelectDropdownField from "shared/form/fields/selectDropdown";
import ButtonType from "shared/enums/ButtonTypes";
import { FormikProps } from "formik";
import ContactMode from "shared/enums/ContactMode";
import { AppType } from "shared/utils/enums";
import { getEnvironmentInfo } from "shared/utils/config";
import { CreateUpdateContactContextProps } from "./types";
import React from "react";
import { useCopies } from "shared/hooks/useCopies";
import FormUnlimitedField from "shared/form/fields/unlimitedField";
import Button from "shared/components/button";
import ToggleButton from "shared/form/core/toggleButton";
import Size from "shared/enums/Size";
import Validation from "shared/form/Validation";
import { useCommonFeatures } from "context/commonFeatures";
import FormTextAreaField from "shared/form/fields/textAreaField";
import { MAX_LICENSE_CAP_FOR_CONTACT_CONTEXT } from "shared/utils/constants";
import { AddParticipantInContextMyExperience } from "apps/myExperience/modules/contactContext/details/components/AddParticipantInContextMyExperience";
import { AddParticipantInConsoleContext } from "apps/console/modules/registration/events/leads/details/components/AddParticipantInConsoleContext";
import AddLicenseOrAdminCriteria from "apps/console/modules/registration/events/leads/details/components/AddLicenseOrAdminCriteria";
import Icon from "assets/icons";
import { DisplayQRCode } from "shared/QRcode/DisplayQRCode";
import Variant from "shared/enums/Variant";
import MessageBar from "shared/components/messageBar";
import { MessageType } from "shared/enums/MessageType";
import FormLabel from "shared/form/fields/formLabel";
import FormCheckboxField from "shared/form/fields/checkboxField";
import { successToast } from "shared/utils/toastNotification";
import ButtonGroup from "shared/components/button/components/buttonGroup";

export function CreateUpdateContactContext(props: CreateUpdateContactContextProps) {
  const service = useContactContextService(props);
  const { navigateBack } = useNavigateWrapper();
  const { initConfirmationAlert } = useCommonFeatures();
  const copies = useCopies();
  const formRef = service.formRef;
  if (service.contextId && !service.leadLocation) {
    return null;
  }

  return (
    <Modal className="leadRetrievalModal" size={Size.LARGE} key={service.leadLocation?.uiVersion}>
      <ModalHeader
        left={service.options.modalHeaderText}
        right={
          <React.Fragment>
            {service.isFormDisabled && props.canShowStatusButtons && (
              <React.Fragment>
                <ToggleButton
                  className="mr-14"
                  label={copies.get("published")}
                  checked={service.leadLocation?.enabled}
                  size={Size.SMALL}
                  onChange={() => {
                    service.handleStatusUpdate();
                  }}
                />
                <Button
                  isOutline
                  variant={Variant.LINK}
                  icon={<Icon.QRCode />}
                  label={copies.get("contact.context.btn.qr.label")}
                  tooltipContent={
                    <DisplayQRCode
                      value={service.leadLocation?.contactContextScanCode || ""}
                      label={service.leadLocation?.label || ""}
                      size={200}
                      downloadSuccessHandler={() =>
                        successToast(copies.get("contact.context.location.qr.download.success.msg"))
                      }
                    />
                  }
                  tooltipConfig={{
                    stylizedInfo: true,
                    animated: true,
                    overlayClassName: "displayQrCodeTooltip"
                  }}
                />
                <Button
                  isOutline
                  variant={Variant.LINK}
                  icon={<Icon.DownloadImage size={18} top={-1} />}
                  onClick={() => {
                    service.handleAttendeeListDownload();
                  }}
                  label={copies.get("attendees")}
                />
                <Button.Edit
                  isOutline
                  variant={Variant.LINK}
                  onClick={() => {
                    service.setIsFormDisabled((c: boolean) => !c);
                  }}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        }
        onClose={() => {
          if (formRef?.current?.dirty) {
            initConfirmationAlert({
              message: copies.get("unsaved.changes.lost.confirmation"),
              handleConfirm: () => {
                navigateBack();
              }
            });
          } else {
            navigateBack();
          }
        }}
      />
      <ModalBody>
        <div className="leadRetrievalForm">
          <FormContainer
            innerRef={formRef}
            enableReinitialize
            onSubmit={values => {
              service.handleCreateUpdate(values);
            }}
            validationSchema={service.validationSchema}
            initialValues={service.initialValues}>
            {(formikProps: FormikProps<typeof service.initialValues>) => {
              return (
                <FormContainer.Form className="leadRetrievalForm_wrapper">
                  {props.contactContextBehavioiur.showAccessCriteriaAsReadOnly() &&
                    formikProps.values.accessCriteriaDescription && (
                      <div className="leadRetrieval_accessCriteriaNote">
                        <MessageBar type={MessageType.INFO}>{formikProps.values.accessCriteriaDescription}</MessageBar>
                      </div>
                    )}
                  <FormTextField
                    disabled={service.isFormDisabled}
                    name={leadCaptureFormFields.LABEL}
                    label={copies.get("context.location.name")}
                    required
                  />
                  {props.contactContextBehavioiur.isLocationTypeFieldVisible() && (
                    <FormSelectDropdownField
                      disabled={service.isFormDisabled}
                      name={leadCaptureFormFields.CONTACT_CONTEXT_LOCATION}
                      label={copies.get("context.location.type")}
                      options={service.options.locationType}
                      required
                    />
                  )}
                  {props.contactContextBehavioiur.isContactModeFieldVisible() &&
                    formikProps.values.contactMode !== ContactMode.LEAD_CAPTURE && (
                      <FormSelectDropdownField
                        disabled={!!service.leadLocation?.id || service.isFormDisabled}
                        name={leadCaptureFormFields.CONTACT_MODE}
                        label={copies.get("context.contact.mode")}
                        options={service.options.contactMode}
                        required
                      />
                    )}
                  {props.contactContextBehavioiur.isContactModeFieldVisible() &&
                    formikProps.values.contactMode === ContactMode.ACCESS_CONTROL && (
                      <FormSelectDropdownField
                        disabled={service.isFormDisabled}
                        name={leadCaptureFormFields.ACCESS_CONTROL_POLICY}
                        label={copies.get("access.control.policy")}
                        infoLabel={copies.get("access.control.policy.tooltip")}
                        options={service.options.accessControlPolicy}
                        validate={(e: any) =>
                          Validation.textFieldValidation({
                            copies,
                            required: true
                          })
                            .nullable()
                            .validate(e)
                            .then(() => undefined)
                            .catch(({ errors }: any) => errors[0])
                        }
                        required
                      />
                    )}
                  {props.contactContextBehavioiur.isContactModeFieldVisible() &&
                    formikProps.values.contactMode === ContactMode.ACCESS_CONTROL && (
                      <FormCheckboxField
                        className="mt-8 mb-16"
                        enableFieldReset
                        disabled={service.isFormDisabled}
                        name={leadCaptureFormFields.ENFORCE_TIME_VALIDATION}
                        label={copies.get("access.control.enforce.time.validation")}
                        infoLabel={copies.get("access.control.enforce.time.validation.tooltip")}
                      />
                    )}
                  {props.eventId && getEnvironmentInfo().appType === AppType.CONSOLE && (
                    <AddParticipantInConsoleContext
                      disabled={service.isFormDisabled}
                      name={leadCaptureFormFields.ADMINS}
                      currentParticipantIds={formikProps.values.admins}
                      participantMap={service.participantMap}
                      eventId={props.eventId}
                      label="contact.context.admins"
                    />
                  )}

                  {props.contactContextBehavioiur.isLicenseFieldVisible() && (
                    <FormUnlimitedField
                      key={service.isFormDisabled.toString()}
                      disabled={service.isFormDisabled}
                      label={copies.get("context.licenses.limit.label")}
                      min={1}
                      max={MAX_LICENSE_CAP_FOR_CONTACT_CONTEXT}
                      name={leadCaptureFormFields.LICENSE_CAP}
                      required
                    />
                  )}
                  {props.contactContextBehavioiur.isCriteriaFieldVisible() && (
                    <div className="leadRetrievalFormGroup leadRetrievalFormGroup--licensees">
                      <div className="leadRetrievalFormGroupHeading">
                        <FormLabel label={copies.get("contact.context.licensees")} />
                      </div>
                      <div className="leadRetrievalFormGroupWrapper">
                        {props.eventId && getEnvironmentInfo().appType === AppType.CONSOLE && (
                          <AddParticipantInConsoleContext
                            disabled={service.isFormDisabled}
                            name={leadCaptureFormFields.LICENSEES}
                            currentParticipantIds={formikProps.values.licensees}
                            participantMap={service.participantMap}
                            eventId={props.eventId}
                            label={"contact.context.licensees"}
                          />
                        )}
                        {formikProps.values.contactMode !== ContactMode.LEAD_CAPTURE && (
                          <AddLicenseOrAdminCriteria
                            isOptional={true}
                            disabled={service.isFormDisabled}
                            name={leadCaptureFormFields.LICENSE_CRITERIA}
                            eventId={props.eventId}
                            api={props.baseApiUtil.getCriteriaFieldsForLicensees()}
                            label="contact.context.license.criteria"
                            infoLabel="contact.context.license.criteria.tooltip">
                            <FormTextAreaField
                              disabled={service.isFormDisabled}
                              name={leadCaptureFormFields.LICENSE_CRITERIA_DESCRIPTION}
                              label={copies.get("license.criteria.desciption")}
                              infoLabel={copies.get("license.criteria.desciption.tooltip")}
                              enableFieldReset={true}
                            />
                          </AddLicenseOrAdminCriteria>
                        )}
                      </div>
                    </div>
                  )}
                  {props.contactContextBehavioiur.isCriteriaFieldVisible() &&
                    formikProps.values.contactMode === ContactMode.ACCESS_CONTROL && (
                      <div className="leadRetrievalFormGroup leadRetrievalFormGroup--accessCriteria">
                        <AddLicenseOrAdminCriteria
                          isOptional={true}
                          disabled={service.isFormDisabled}
                          name={leadCaptureFormFields.ACCESS_CRITERIA}
                          warningMessage={
                            !formikProps.values.accessCriteria_enabled
                              ? copies.get("context.access.criteria.not.set.warning")
                              : ""
                          }
                          eventId={props.eventId}
                          api={props.baseApiUtil.getCriteriaFieldsForAccessControl()}
                          label="contact.context.access.criteria"
                          infoLabel="contact.context.access.criteria.tooltip">
                          <FormTextAreaField
                            disabled={service.isFormDisabled}
                            name={leadCaptureFormFields.ACCESS_CRITERIA_DESCRIPTION}
                            label={copies.get("access.criteria.desciption")}
                            infoLabel={copies.get("access.criteria.desciption.tooltip")}
                            enableFieldReset={true}
                          />
                        </AddLicenseOrAdminCriteria>
                      </div>
                    )}
                  {getEnvironmentInfo().appType === AppType.MYEXPERIENCE &&
                    service.leadLocation &&
                    service.participants && (
                      <React.Fragment>
                        <AddParticipantInContextMyExperience
                          disabled={service.isFormDisabled}
                          key={service.leadLocation.uiVersion + service.participants.length}
                          name={leadCaptureFormFields.ADMINS}
                          currentParticipantIds={formikProps.values.admins}
                          participants={service.participants || []}
                          participantMap={service.participantMap}
                          requesterId={props.requesterId}
                          label="contact.context.admins"
                          licenseCap={formikProps.values.licenseCap}
                          unlimitedLicenseCap={formikProps.values.unlimitedLicenseCap}
                          assigned={service.initialValues.licensees.length}
                          allowRemoveFromOtherOrgs={false}
                        />
                        <AddParticipantInContextMyExperience
                          disabled={service.isFormDisabled}
                          name={leadCaptureFormFields.LICENSEES}
                          currentParticipantIds={formikProps.values.licensees}
                          participants={service.participants || []}
                          participantMap={service.participantMap}
                          requesterId={props.requesterId}
                          canRemoveSelf={true}
                          label={"contact.context.licensees"}
                          showLicenseCap={true}
                          licenseCap={formikProps.values.licenseCap}
                          unlimitedLicenseCap={formikProps.values.unlimitedLicenseCap}
                          assigned={service.initialValues.licensees.length}
                          allowRemoveFromOtherOrgs={true}
                          tableNote={service.initialValues.licenseCriteriaDescription}
                        />
                      </React.Fragment>
                    )}
                </FormContainer.Form>
              );
            }}
          </FormContainer>
        </div>
      </ModalBody>
      {!service.isFormDisabled && (
        <ModalFooter hasFooterBorder>
          <ButtonGroup fullWidth>
            <Button.Save
              type={ButtonType.SUBMIT}
              onClick={() => {
                formRef.current?.handleSubmit();
              }}
            />
            {!service.leadLocation?.enabled && (
              <Button.Save
                type={ButtonType.SUBMIT}
                label={copies.get("save.and.publish")}
                onClick={() => {
                  formRef.current?.setFieldValue("enabled", true);
                  formRef.current?.handleSubmit();
                }}
              />
            )}
            {props.contactContextBehavioiur.showCancelButton() && service.leadLocation?.id && (
              <Button
                label={copies.get("cancel")}
                variant={Variant.ERROR}
                isOutline={true}
                onClick={e => {
                  formRef.current?.resetForm();
                  service.setIsFormDisabled(true);
                }}
              />
            )}
          </ButtonGroup>
        </ModalFooter>
      )}
    </Modal>
  );
}
